import type { UserRole } from './types/workspace';

export type CanonicalRole = 'consultor_master' | 'admin' | 'gestor' | 'usuario' | 'viewer';
export type Capability =
  | 'admin.access'
  | 'users.manage'
  | 'plans.manage'
  | 'org.manage'
  | 'billing.view'
  | 'billing.manage'
  | 'workflow.full_access'
  | 'workflow.manage'
  | 'tasks.execute'
  | 'dashboard.view'
  | 'dashboard.view_limited'
  | 'view.simulation'
  | 'org_structure.view'
  | 'org_structure.manage'
  | 'roles.manage'
  | 'departments.manage'
  | 'user_assignment.manage';

type RoleCapabilityMap = Record<CanonicalRole, Capability[]>;

export const ROLE_CAPABILITIES: RoleCapabilityMap = {
  consultor_master: [
    'admin.access',
    'users.manage',
    'plans.manage',
    'org.manage',
    'billing.view',
    'billing.manage',
    'workflow.full_access',
    'view.simulation',
    'dashboard.view',
    'dashboard.view_limited',
    'org_structure.view',
    'org_structure.manage',
    'roles.manage',
    'departments.manage',
    'user_assignment.manage',
  ],
  admin: [
    'users.manage',
    'workflow.full_access',
    'billing.view',
    'dashboard.view',
    'org_structure.view',
    'org_structure.manage',
    'roles.manage',
    'departments.manage',
    'user_assignment.manage',
  ],
  gestor: [
    'workflow.manage',
    'dashboard.view',
    'org_structure.view',
    'user_assignment.manage',
  ],
  usuario: [
    'tasks.execute',
    'dashboard.view_limited',
  ],
  viewer: [
    'dashboard.view',
    'dashboard.view_limited',
  ],
};

const ROLE_ALIASES: Record<string, CanonicalRole> = {
  owner: 'consultor_master',
  consultor_master: 'consultor_master',
  admin: 'admin',
  administrador: 'admin',
  consultant: 'admin',
  consultant_master: 'consultor_master',
  manager: 'gestor',
  gestor: 'gestor',
  operator: 'usuario',
  operador: 'usuario',
  usuario: 'usuario',
  user: 'usuario',
  viewer: 'viewer',
  visualizador: 'viewer',
};

export type ViewModeLike = 'auto' | 'admin' | 'gestor' | 'usuario';

export interface CapabilityTarget {
  role?: UserRole | string | null;
  view_mode?: ViewModeLike | string | null;
}

export interface CapabilityCheckOptions {
  realRoleOnly?: boolean;
}

export function normalizeRole(role?: string | null): CanonicalRole | null {
  if (!role) return null;
  const normalized = ROLE_ALIASES[role as keyof typeof ROLE_ALIASES];
  return normalized ?? null;
}

export function getEffectiveRole(
  target?: CapabilityTarget | null,
  options: CapabilityCheckOptions = {}
): CanonicalRole | null {
  const realRole = normalizeRole(target?.role ?? null);
  if (!realRole) return null;

  return realRole;
}

export function hasCapability(
  target: CapabilityTarget | null | undefined,
  capability: Capability,
  options: CapabilityCheckOptions = {}
): boolean {
  if (!target) return false;

  const realRole = normalizeRole(target.role ?? null);
  if (!realRole) return false;

  const roleToCheck = options.realRoleOnly
    ? realRole
    : getEffectiveRole(target, options) ?? realRole;

  return ROLE_CAPABILITIES[roleToCheck]?.includes(capability) ?? false;
}

export function getLostCapabilities(realRole: string | null | undefined, simulatedRole: string | null | undefined) {
  const real = normalizeRole(realRole);
  const simulated = normalizeRole(simulatedRole);

  if (!real) return [];
  if (!simulated) return [];
  if (real === simulated) return [];

  const realSet = new Set(ROLE_CAPABILITIES[real] ?? []);
  const simulatedSet = new Set(ROLE_CAPABILITIES[simulated] ?? []);

  return Array.from(realSet).filter(capability => !simulatedSet.has(capability));
}
